import { useMemo } from "react";
import { useEditItem2, useNewItem } from "../../toolympus/api/useNewItem";
import { useBrowserStoredValue } from "../../toolympus/hooks/useBrowserStoredValue";
import { useLoadedData } from "../../toolympus/hooks/useLoadedData";
import { FieldType, mergeSchema, Schema } from "../../toolympus/hooks/useSchema";
import { useSmartList } from "../../toolympus/hooks/useSmartList";
import { FieldDefinition } from "../../toolympus/components/schemed";
import { useItemActionWithConfirmation } from "../../toolympus/api/useAction";
import { apiFetch } from "../../toolympus/api/core";
import { useDictionaries } from "../../toolympus/hooks/useDictionaries";

export interface LegacyResult {
  _id: number;
  year: string;
  kind: string;
  university?: string;
  position?: string;
  holder?: string;
  
  result1?: string;
  result2?: string;
  result3?: string;
  result4?: string;
  result5?: string;
}

export interface LegacyResultConfig {
  kind: string;
  title: string;
  labels?: Record<string, string>;
}


export const LegacyResultSchema: Schema = {
  kind: { label: "Kind" },
  year: { label: "Year", type: FieldType.dictionarySelect, dictionary: "LegacyYears" },
  university: { label: "University", type: FieldType.dictionarySelect, dictionary: "Universities" },
  position: { label: "Position", type: FieldType.number },
}


export const LegacyResultConfigSchema: Schema = {
  kind: { label: "Kind" },
  title: { label: "Title" },
  sort_order: { label: "Sorting", type: FieldType.number },
}

export const LegacyResultConfigLabelsSchema: Schema = {
  result1: { label: "Result 1 label" },
  result2: { label: "Result 2 label" },
  result3: { label: "Result 3 label" },
  result4: { label: "Result 4 label" },
  result5: { label: "Result 5 label" },
  holder: { label: "Holder label" },
}

export const LegacyResultApiPath = "/api/legacy-results";

const ConfigurableFields = ["holder", "result1", "result2", "result3", "result4", "result5"];


export const useAdminLegacyResults = () => {
  const configs = useLoadedData<LegacyResultConfig[]>(`${LegacyResultApiPath}/config`, []);

  const editConfig = useEditItem2<LegacyResultConfig>({
    getApiPath: r => `${LegacyResultApiPath}/config/${r.kind}`,
    dontResetOnSave: true,
  });

  const newConfig = useNewItem<Partial<LegacyResultConfig>, LegacyResultConfig>(`${LegacyResultApiPath}/config`, { kind: "", title: "", labels: {} });

  const selectedKind = useBrowserStoredValue(configs.data[0]?.kind || "", "kind", "__j_lr_kind");

  const resultsYear = useBrowserStoredValue("", "year", "__j_lr_year");
  const results = useSmartList<LegacyResult>(`${LegacyResultApiPath}/admin`, {
    schema: LegacyResultSchema,
    newRecordDefault: { year: resultsYear.value || "", kind: selectedKind.value },
    extraParams: resultsYear.value ? {
      kind: selectedKind.value,
      year: resultsYear.value,
    } : {
      kind: selectedKind.value,
    },
    getRecordApiPath: r => `${LegacyResultApiPath}/admin/${r._id}`,
    noLimit: true,
    noCount: true,
  })

  const resultsMeta = useMemo(() => {
    const config = configs.data.find(x => x.kind === selectedKind.value);
    if(!config) {
      return {
        config: null,
        fields: [],
        schema: LegacyResultConfigSchema,
      }
    }

    const labels = config.labels || {};

    const fields: FieldDefinition[] = [
      ["position"],
      ["university", { autoComplete: true }],
      ...ConfigurableFields.map(f => {
        const label = labels[f];
        if(label) {
          return [f, { label }];
        } else {
          return null;
        }
      }).filter(x => !!x) as FieldDefinition[],
    ];

    const schemaAdds: Schema = {};
    ConfigurableFields.forEach(f => {
      schemaAdds[f] = { label: labels[f] };
    })

    const schema = mergeSchema(LegacyResultSchema, schemaAdds);

    return {
      config,
      fields,
      schema,
    }

  }, [configs.data, selectedKind.value]);

  const removeConfig = useItemActionWithConfirmation(async (item: LegacyResultConfig) => {
    await apiFetch(`${LegacyResultApiPath}/config/${item.kind}`, "delete");
    if(editConfig.item?.kind === item.kind) {
      editConfig.cancel();
    }
    configs.reload();
  }, {
    memoTrigger: editConfig.item,
    title: "Delete kind",
  });

  const importResults = useLegacyResultsImport(selectedKind.value, resultsYear.value, resultsMeta.schema, () => results.reload());

  return {
    configs,
    newConfig: {
      ...newConfig,
      save: (extraChanges?: Partial<LegacyResultConfig>) => newConfig.save(extraChanges).then(x => { configs.reload(); return x; })
        .then(x => { configs.reload(); editConfig.startEditing({ ...x }); return x; }),
    },
    editConfig: {
      ...editConfig,
      save: (extraChanges?: Partial<LegacyResultConfig>) => editConfig.save(extraChanges),
    },
    removeConfig,

    configSchema: LegacyResultConfigSchema,
    configLabelsSchema: LegacyResultConfigLabelsSchema,

    selectedKind,
    results,
    resultsYear: resultsYear,
    resultsSchema: resultsMeta.schema,
    resultsFields: resultsMeta.fields,
    importResults,
  }

}




const UniverisitiesDictionaryKey = "Universities";
const UniFinalField = "uni_final";

export const useLegacyResultsImport = (kind: string, year: string, schema: Schema, reloadList: () => void) => {
  
  const importResults = useEditItem2<{ textIn: string, records: string[][], recordsPreprocessed: Record<string, any>[], fieldsMapping: (string | null)[], nCols: number }>({
    save: async (item) => {
      if(item.recordsPreprocessed && kind && year) {
        const records = item.recordsPreprocessed.map(r => {
          const x = { ...r };
          x.university = r[UniFinalField];
          delete x[UniFinalField];
          return x;
        });
        await apiFetch(`${LegacyResultApiPath}/admin/import`, "post", { records });
        reloadList();
      }
      return item;
    }
  })

  const dictionaries = useDictionaries();
  const unisDict = dictionaries[UniverisitiesDictionaryKey];

  const fieldsOptions = [
    ...["position", "university"].map(f => ({ field: f, label: schema[f]?.label })),
    ...ConfigurableFields.map(f => ({ field: f, label: schema[f]?.label })).filter(f => !!f.label)
  ];

  const stage3FieldsOptions = [
    ...["position", "university"].map(f => ({ field: f, label: schema[f]?.label })),
    { field: UniFinalField, label: "University proper", type: FieldType.dictionarySelect, dictionary: UniverisitiesDictionaryKey },
    ...ConfigurableFields.map(f => ({ field: f, label: schema[f]?.label })).filter(f => !!f.label)
  ];

  const process1 = () => {
    if(!importResults.item?.textIn) {
      return;
    }

    const records = importResults.item?.textIn
      .split('\n')
      .filter(line => line.trim() !== '') // Ignore empty lines
      .map(line => line.split('\t').map(s => s.trim()));
    const nCols = Math.max(0, ...records.map(r => r.length));
    importResults.update({
      records,
      nCols,
      fieldsMapping: Array.from({ length: nCols}).map(() => ""),
    })
  };

  const process2 = () => {
    if(!importResults.item?.records?.length) {
      return [];
    }
    const fieldsMapping = importResults.item.fieldsMapping;
    const recordsPreprocessed: Record<string,any>[] = importResults.item.records.map(r => {
      let rr: any = { kind: kind, year: year };
      fieldsMapping.forEach((fx,idx) => {
        if(fx) {
          rr[fx] = r[idx];
        }
      });
      rr[UniFinalField] = unisDict?.records?.find(u => !!rr["university"] && rr["university"]?.toLowerCase() === u.label.toLowerCase())?.code;
      
      return rr;
    });

    importResults.update({
      recordsPreprocessed,
    })
  }

  const updateRowUniversity = (rowIdx: number, value: string) => {
    if(!importResults.item || !importResults.item.recordsPreprocessed?.length) {
      return;
    }

    importResults.update({
      recordsPreprocessed: importResults.item.recordsPreprocessed.map((r,idx) => idx === rowIdx ? { ...r, [UniFinalField]: value } : r),
    })
  }


  return {
    ...importResults,
    
    start: () => {
      importResults.startEditing({ records: [], recordsPreprocessed: [], textIn: "", nCols: 0, fieldsMapping: [] });
    },
    
    stage: importResults.item?.recordsPreprocessed?.length
      ? "stage3"
      : importResults.item?.records?.length
        ? "stage2"
        : "stage1",

    process1,

    process2,
    updateRowUniversity,


    finish: () => importResults.save(),
    canFinish: importResults.item?.recordsPreprocessed?.length && !importResults.item.recordsPreprocessed.find(r => !r[UniFinalField]),
    uniFinalField: UniFinalField,
    
    restart: () => {
      importResults.update(importResults.item?.recordsPreprocessed?.length 
        ? { recordsPreprocessed: [] }
        : { records: [], nCols: 0, fieldsMapping: [], recordsPreprocessed: [] });
    },


    fieldsOptions,
    stage3FieldsOptions,
  }
}


export type ImportLegacyResultsData = ReturnType<typeof useLegacyResultsImport>;
